import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { matchPath } from 'react-router-dom';
import type { SessionRecovery } from '@/modules/common/api/session-recovery/controller.ts';
import { publicPages } from '@/modules/common/routes.tsx';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _sessionRecoveryAttempted?: boolean;
};

const shouldRecover = () => !publicPages.some(path => matchPath(path, location.pathname));

export const attachSessionRecoveryInterceptors = (
  instance: AxiosInstance,
  sessionRecovery: SessionRecovery,
) => {
  instance.interceptors.request.use(config => {
    if (sessionRecovery.isRedirecting()) {
      return Promise.reject(new Error('Session redirect is in progress'));
    }
    return config;
  });

  instance.interceptors.response.use(undefined, async error => {
    const config = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    if (
      !config ||
      config._sessionRecoveryAttempted ||
      !shouldRecover() ||
      ![401, 403, 419].includes(status)
    ) {
      throw error;
    }

    if (status === 419) {
      await sessionRecovery.expire();
      throw error;
    }

    config._sessionRecoveryAttempted = true;
    const recovered = await sessionRecovery.recover();
    if (!recovered) throw error;

    config.headers.delete('Authorization');
    return instance.request(config);
  });
};
